(() => {
  'use strict';

  const INITIALISED_FLAG = '__bookclubInitialised';
  const BUSY_CLASS = 'bookclub-is-busy';
  const ADDED_CLASS = 'bookclub-is-added';
  const REFERRAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

  function getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie ? document.cookie.split('; ') : [];

    for (const cookie of cookies) {
      if (cookie.startsWith(prefix)) {
        try {
          return decodeURIComponent(cookie.substring(prefix.length));
        } catch (error) {
          return cookie.substring(prefix.length);
        }
      }
    }

    return '';
  }

  function setCookie(name, value, maxAge = REFERRAL_COOKIE_MAX_AGE) {
    if (!name || !value) {
      return;
    }

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    cookie += '; Path=/';
    cookie += `; Max-Age=${maxAge}`;
    cookie += '; SameSite=Lax';

    if (window.location.protocol === 'https:') {
      cookie += '; Secure';
    }

    document.cookie = cookie;
  }

  function normalise(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function ensureAffiliateReferralCookie(container) {
    const cookieName =
      normalise(container.dataset.referralCookie) || 'saref';
    const existingReferralId = normalise(getCookie(cookieName));
    const loggedInAffiliateReferralId =
      normalise(container.dataset.loggedInAffiliateReferralId);

    if (!existingReferralId && loggedInAffiliateReferralId) {
      setCookie(cookieName, loggedInAffiliateReferralId);
      container.dataset.referralId = loggedInAffiliateReferralId;
      return loggedInAffiliateReferralId;
    }

    return existingReferralId || normalise(container.dataset.referralId);
  }

  function setMessage(container, message, type = '') {
    const element = container.querySelector('[data-bookclub-message]');

    if (!element) {
      return;
    }

    element.textContent = message || '';
    element.classList.remove(
      'bookclub-message--success',
      'bookclub-message--error',
      'bookclub-message--info'
    );

    if (type) {
      element.classList.add(`bookclub-message--${type}`);
    }
  }

  function setBusy(container, busy) {
    const buttons = container.querySelectorAll(
      '[data-bookclub-add], [data-bookclub-remove]'
    );

    container.classList.toggle(BUSY_CLASS, busy);

    buttons.forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  }

  function setAddedState(container, added) {
    const addButton = container.querySelector('[data-bookclub-add]');
    const removeButton = container.querySelector('[data-bookclub-remove]');

    container.classList.toggle(ADDED_CLASS, added);
    container.dataset.bookclubAdded = added ? 'true' : 'false';

    if (addButton) {
      addButton.hidden = added;
    }

    if (removeButton) {
      removeButton.hidden = !added;
    }
  }

  function getRequestData(container) {
    const productGid = normalise(container.dataset.productGid);
    const customerId = normalise(container.dataset.customerId);
    const referralId = ensureAffiliateReferralCookie(container);
    const clubSlug = normalise(container.dataset.clubSlug);

    if (!productGid) {
      throw new Error('The product GID is missing.');
    }

    if (!/^gid:\/\/shopify\/Product\/\d+$/.test(productGid)) {
      throw new Error('The product GID is invalid.');
    }

    if (!referralId) {
      throw new Error('No Book Club referral ID was found.');
    }

    if (!clubSlug) {
      throw new Error('No Book Club slug was found.');
    }

    return {
      product_gid: productGid,
      referral_id: referralId,
      customer_id: customerId,
      clubSlug: clubSlug
    };
  }

  async function postAction(container, action) {
    const endpoint =
      action === 'remove'
        ? normalise(container.dataset.removeEndpoint)
        : normalise(container.dataset.addEndpoint);

    if (!endpoint) {
      throw new Error(`The ${action} endpoint is missing.`);
    }

    const requestData = getRequestData(container);
    const body = new URLSearchParams();

    body.set('product_gid', requestData.product_gid);
    body.set('referral_id', requestData.referral_id);
    body.set('customer_id', requestData.customer_id);
    body.set('clubSlug', requestData.clubSlug);

    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body.toString()
    });

    const contentType = response.headers.get('content-type') || '';
    let payload;

    if (contentType.includes('application/json')) {
      payload = await response.json();
    } else {
      const responseText = await response.text();
      throw new Error(
        responseText
          ? `The server returned HTML or text instead of JSON: ${responseText.slice(0, 160)}`
          : `The server returned HTTP ${response.status}.`
      );
    }

    if (!response.ok || payload?.ok !== true) {
      throw new Error(
        payload?.message ||
        payload?.error ||
        `The server returned HTTP ${response.status}.`
      );
    }

    return payload;
  }

  async function handleAction(container, action) {
    if (container.classList.contains(BUSY_CLASS)) {
      return;
    }

    setBusy(container, true);
    setMessage(
      container,
      action === 'remove' ? 'Removing book…' : 'Adding book…',
      'info'
    );

    try {
      await postAction(container, action);

      const added = action !== 'remove';
      setAddedState(container, added);
      setMessage(
        container,
        added
          ? 'This book has been added to the Book Club.'
          : 'This book has been removed from the Book Club.',
        'success'
      );
    } catch (error) {
      console.error('[Book Club]', error);
      setMessage(
        container,
        error instanceof Error ? error.message : 'Something went wrong.',
        'error'
      );
    } finally {
      setBusy(container, false);
    }
  }

  function initialiseContainer(container) {
    if (container[INITIALISED_FLAG]) {
      return;
    }

    container[INITIALISED_FLAG] = true;

    ensureAffiliateReferralCookie(container);

    const addButton = container.querySelector('[data-bookclub-add]');
    const removeButton = container.querySelector('[data-bookclub-remove]');

    if (addButton) {
      addButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleAction(container, 'add');
      });
    }

    if (removeButton) {
      removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleAction(container, 'remove');
      });
    }

    setAddedState(
      container,
      normalise(container.dataset.bookclubAdded).toLowerCase() === 'true'
    );
  }

  function initialise(root = document) {
    root
      .querySelectorAll('[data-bookclub-container]')
      .forEach(initialiseContainer);
  }

  function start() {
    initialise(document);

    document.addEventListener('shopify:section:load', (event) => {
      initialise(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
